import {
  faCheck,
  faCopy,
  faEdit,
  faExternalLinkAlt,
  faLink,
  faPlus,
  faStore,
  faTrashCan,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import { CanView } from "../../../components/CanView/CanView"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import THEME from "../../../configuration/Theme"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { SIZE } from "../../../configuration/Sizes"
import { Permissions } from "../../../Enum/PermissionsType"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { useGetStoreGroups } from "../../../hooks/StoreGroups/useGetStoreGroups"
import { useSaveStoreGroup, type StoreGroupSaveRequest } from "../../../hooks/StoreGroups/useSaveStoreGroup"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"
import type { StoreGroup } from "../../../Objects/StoreGroups/StoreGroup"
import type { StoreGroupInvite } from "../../../Objects/StoreGroups/StoreGroupInvite"
import { normalizeSlug } from "../../../utils/slug"
import { StoreGroupPopup } from "./StoreGroupPopup"

const storeGroupAnimationDuration = "1.6s"

const getStatusKey = (status: string | number) => {
  if (typeof status === "number") {
    return ["pending", "accepted", "rejected", "revoked"][status] ?? "pending"
  }

  return status.toLowerCase()
}

const getStatusBadgeClass = (status: string | number) =>
  getStatusKey(status) === "pending"
    ? "border-amber-500 bg-amber-100 text-amber-900 shadow-sm"
    : "border-slate-200 bg-slate-100 text-slate-600"

export const StoreGroupsPage = () => {
  const { t } = useTranslation(["company", "helpPopup"])

  const [groups, setGroups] = useState<StoreGroup[]>([])

  const [invites, setInvites] = useState<StoreGroupInvite[]>([])

  const [inviteSlugByGroup, setInviteSlugByGroup] = useState<Record<number, string>>({})

  const [popupGroup, setPopupGroup] = useState<StoreGroup | null | undefined>(undefined)

  const [loading, setLoading] = useState(false)

  const [message, setMessage] = useState("")

  const [error, setError] = useState("")

  const canEditCompany = usePermission(Permissions.CanEditeCompany)

  const { handleGetStoreGroups, handleGetStoreGroupInvites } = useGetStoreGroups()

  const {
    handleCreateStoreGroup,
    handleUpdateStoreGroup,
    handleDeleteStoreGroup,
    handleInviteStore,
    handleAcceptInvite,
    handleRejectInvite,
    handleRevokeMember,
  } = useSaveStoreGroup()

  useQuickActionPopup("store-group-create", () => setPopupGroup(null), canEditCompany)

  const load = async () => {
    setLoading(true)

    setError("")

    try {
      const [nextGroups, nextInvites] = await Promise.all([handleGetStoreGroups(), handleGetStoreGroupInvites()])

      setGroups(nextGroups)

      setInvites(nextInvites)
    } catch (err) {
      setError(err instanceof Error ? err.message : t("company:store_groups.load_error"))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const getGroupUrl = (groupSlug: string) => `${API_ROUTES.SITE}g/${groupSlug}`

  const copyGroupLink = async (groupSlug: string) => {
    await navigator.clipboard.writeText(getGroupUrl(groupSlug))

    setMessage(t("company:store_groups.link_copied"))
  }

  const openGroupLink = (groupSlug: string) => {
    window.open(getGroupUrl(groupSlug), "_blank", "noopener,noreferrer")
  }

  const saveGroup = async (request: StoreGroupSaveRequest & { isActive: boolean }) => {
    if (!canEditCompany) {
      return false
    }

    setError("")

    setMessage("")

    const saved = popupGroup
      ? await handleUpdateStoreGroup(popupGroup.id, request)
      : await handleCreateStoreGroup(request)

    if (!saved) {
      return false
    }

    setMessage(popupGroup ? t("company:store_groups.group_updated") : t("company:store_groups.group_created"))

    await load()

    return true
  }

  const deleteGroup = async () => {
    if (!popupGroup || !canEditCompany) {
      return false
    }

    setError("")

    setMessage("")

    const deleted = await handleDeleteStoreGroup(popupGroup.id)

    if (!deleted) {
      return false
    }

    setMessage(t("company:store_groups.group_deleted"))

    await load()

    return true
  }

  const inviteStore = async (groupId: number) => {
    if (!canEditCompany) {
      return
    }

    const storeSlug = inviteSlugByGroup[groupId]?.trim()

    if (!storeSlug) {
      setError(t("company:store_groups.invalid_invite"))
      return
    }

    setError("")

    setMessage("")

    const invited = await handleInviteStore(groupId, storeSlug)

    if (!invited) {
      return
    }

    setInviteSlugByGroup((current) => ({ ...current, [groupId]: "" }))

    setMessage(t("company:store_groups.invite_created"))

    await load()
  }

  const respondInvite = async (memberId: number, accept: boolean) => {
    if (!canEditCompany) {
      return
    }

    setError("")

    setMessage("")

    const responded = accept ? await handleAcceptInvite(memberId) : await handleRejectInvite(memberId)

    if (!responded) {
      return
    }

    setMessage(accept ? t("company:store_groups.invite_accepted") : t("company:store_groups.invite_rejected"))
    await load()
  }

  const revokeMember = async (memberId: number) => {
    if (!canEditCompany) {
      return
    }

    setError("")

    setMessage("")

    const revoked = await handleRevokeMember(memberId)

    if (!revoked) {
      return
    }

    setMessage(t("company:store_groups.member_revoked"))
    await load()
  }

  return (
    <div className="h-[calc(100%-25px)] w-full max-w-full overflow-x-hidden overflow-y-auto p-2 sm:p-4 md:p-2">
      <Animate type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
        <div className="mb-3 mt-3 p-2">
          <div className="flex flex-row items-center justify-between gap-2">
            <h2 className="flex min-w-0 flex-1 items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                <FontAwesomeIcon icon={faStore} className="text-sm" />
              </span>

              <span className="truncate">{t("company:store_groups.title")}</span>
            </h2>

            <div className="flex shrink-0 items-center gap-2">
              <CanView permission={Permissions.CanEditeCompany}>
                <div className="rounded-[7px] bg-white/10 backdrop-blur-[1px]">
                  <ButtonIcon
                    typeButton="button"
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    icon={faPlus}
                    onClick={() => setPopupGroup(null)}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.setPopupGroupNull"
                  />
                </div>
              </CanView>

              <PopupHelpButton
                variant="global"
                helpKey="pages.store-groups"
                title={t("helpPopup:global.storeGroups.title")}
                description={t("helpPopup:global.storeGroups.description")}
                bullets={t("helpPopup:global.storeGroups.bullets", { returnObjects: true }) as string[]}
                steps={t("helpPopup:global.storeGroups.steps", { returnObjects: true }) as string[]}
              />
            </div>
          </div>
        </div>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
        <p className="mb-3 px-2 text-sm font-medium leading-relaxed text-slate-600">
          {t("company:store_groups.description")}
        </p>
      </Animate>

      {message && (
        <Animate type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
          <div className="mb-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-700">
            {message}
          </div>
        </Animate>
      )}

      {error && (
        <Animate type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
          <div className="mb-3 rounded-lg border-2 border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">
            {error}
          </div>
        </Animate>
      )}

      {invites.length > 0 && (
        <Animate type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
          <section className="mb-4 rounded-lg border-2 border-amber-200 bg-white p-3">
            <strong className="text-sm text-amber-900">{t("company:store_groups.pending_invites")}</strong>

            <div className="mt-3 grid gap-2">
              {invites.map((invite) => (
                <Animate
                  key={invite.memberId}
                  type={AnimationTypes.fadeInTraslate}
                  duration={storeGroupAnimationDuration}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-bold text-slate-900">{invite.groupName}</p>

                      <p className="text-xs font-bold text-slate-500">/g/{invite.groupSlug}</p>
                    </div>

                    <CanView permission={Permissions.CanEditeCompany}>
                      <div className="grid gap-2 sm:flex sm:justify-end">
                        <button
                          type="button"
                          onClick={() => respondInvite(invite.memberId, true)}
                          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-3 text-xs font-bold text-emerald-800 shadow-sm transition hover:border-emerald-400 hover:bg-emerald-100 sm:w-auto"
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.respondInviteInviteMemberIdTrue"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                          {t("company:store_groups.accept")}
                        </button>

                        <button
                          type="button"
                          onClick={() => respondInvite(invite.memberId, false)}
                          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border-2 border-red-300 bg-red-50 px-3 text-xs font-bold text-red-700 shadow-sm transition hover:border-red-400 hover:bg-red-100 sm:w-auto"
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.respondInviteInviteMemberIdFalse"
                        >
                          <FontAwesomeIcon icon={faXmark} />
                          {t("company:store_groups.reject")}
                        </button>
                      </div>
                    </CanView>
                  </div>
                </Animate>
              ))}
            </div>
          </section>
        </Animate>
      )}

      <Animate type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
        <section className="grid gap-3">
          {groups.length === 0 ? (
            <div className="rounded-lg border-2 border-slate-200 bg-white p-5 text-sm font-bold text-slate-500">
              {loading ? t("company:store_groups.loading") : t("company:store_groups.empty")}
            </div>
          ) : (
            groups.map((group) => (
              <Animate key={group.id} type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
                <article className="rounded-lg border-2 border-slate-200 bg-white p-3 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 gap-3">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-slate-100 bg-slate-50">
                        {group.iconUrl ? (
                          <img
                            src={group.iconUrl.toClientRequest()}
                            alt={group.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <FontAwesomeIcon icon={faStore} className="text-xl text-orange-600" />
                        )}
                      </div>

                      <div className="min-w-0">
                        <h3 className="truncate text-base font-bold text-slate-900">{group.name}</h3>

                        <p className="mt-1 truncate text-xs font-bold text-slate-500">{getGroupUrl(group.slug)}</p>

                        {group.description && (
                          <p className="mt-1 line-clamp-2 text-xs font-medium text-slate-600">{group.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid w-full gap-2 sm:flex sm:w-auto sm:justify-end">
                      <button
                        type="button"
                        title={t("company:store_groups.copy")}
                        aria-label={t("company:store_groups.copy")}
                        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 text-xs font-bold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 sm:w-10 sm:px-0"
                        onClick={() => copyGroupLink(group.slug)}
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.copy"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                        <span className="sm:hidden">{t("company:store_groups.copy")}</span>
                      </button>

                      <button
                        type="button"
                        title={t("company:store_groups.open")}
                        aria-label={t("company:store_groups.open")}
                        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-3 text-xs font-bold text-emerald-800 shadow-sm transition hover:border-emerald-400 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:w-10 sm:px-0"
                        onClick={() => openGroupLink(group.slug)}
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.open"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        <span className="sm:hidden">{t("company:store_groups.open")}</span>
                      </button>

                      {group.canManage && (
                        <CanView permission={Permissions.CanEditeCompany}>
                          <button
                            type="button"
                            title={t("company:store_groups.edit")}
                            aria-label={t("company:store_groups.edit")}
                            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border-2 border-orange-300 bg-orange-50 px-3 text-xs font-bold text-orange-800 shadow-sm transition hover:border-orange-400 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-200 sm:w-10 sm:px-0"
                            onClick={() => setPopupGroup(group)}
                            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                            <span className="sm:hidden">{t("company:store_groups.edit")}</span>
                          </button>
                        </CanView>
                      )}
                    </div>
                  </div>

                  {group.canManage && (
                    <Animate type={AnimationTypes.fadeInTraslate} duration={storeGroupAnimationDuration}>
                      <CanView permission={Permissions.CanEditeCompany}>
                        <div className="mt-4 grid w-full max-w-full grid-cols-1 items-center gap-2 overflow-hidden sm:grid-cols-[minmax(0,1fr)_auto]">
                          <div className="min-w-0 max-w-full overflow-hidden p-2 *:w-full *:min-w-0 *:max-w-full">
                            <InputField
                              type="text"
                              value={inviteSlugByGroup[group.id] ?? ""}
                              placeholder={t("company:store_groups.invite_placeholder")}
                              icon={faLink}
                              maxLength={100}
                              onChange={(event) =>
                                setInviteSlugByGroup((current) => ({
                                  ...current,
                                  [group.id]: normalizeSlug(event.target.value),
                                }))
                              }
                              descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.input.invitePlaceholder"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => inviteStore(group.id)}
                            className="inline-flex h-[42px] w-full items-center justify-center gap-2 rounded-xl border-2 border-orange-300 bg-orange-50 px-4 text-sm font-bold text-orange-800 shadow-sm transition hover:border-orange-400 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-200 sm:w-auto sm:min-w-[150px]"
                            data-interaction-description-key="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.inviteStoreGroupId"
                          >
                            <FontAwesomeIcon icon={faPlus} />
                            {t("company:store_groups.invite")}
                          </button>
                        </div>
                      </CanView>
                    </Animate>
                  )}

                  <div className="mt-4 grid gap-2">
                    {group.stores.map((store) => (
                      <Animate
                        key={store.memberId}
                        type={AnimationTypes.fadeInTraslate}
                        duration={storeGroupAnimationDuration}
                      >
                        <div className="flex w-full items-start justify-between gap-2 rounded-lg border-2 border-orange-100 p-3">
                          <div className="flex w-full min-w-0 items-start gap-3">
                            <FontAwesomeIcon icon={faStore} className="text-orange-600" />

                            <div className="min-w-0">
                              <p className="truncate font-bold text-slate-900">{store.companyName}</p>

                              <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                                <span className="truncate text-xs font-bold text-slate-500">/{store.slug}</span>

                                <span
                                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-black uppercase ${getStatusBadgeClass(
                                    store.status,
                                  )}`}
                                >
                                  {t(`company:store_groups.status.${getStatusKey(store.status)}`)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {store.canRevoke && (
                            <CanView permission={Permissions.CanEditeCompany}>
                              <ButtonIcon
                                sizeNumber={SIZE.buttonExpandIcon}
                                bgColor={THEME.colors.buttonBGRemoveColor}
                                iconColor={THEME.colors.buttonFGRemoveColor}
                                icon={faTrashCan}
                                onClick={() => revokeMember(store.memberId)}
                                descriptionKey="interactionDescriptions:pages.myRestaurant.storeGroups.storeGroupsPage.button.revokeMemberStoreMemberId"
                              />
                            </CanView>
                          )}
                        </div>
                      </Animate>
                    ))}
                  </div>
                </article>
              </Animate>
            ))
          )}
        </section>
      </Animate>

      {popupGroup !== undefined && (
        <StoreGroupPopup
          group={popupGroup}
          onClose={() => setPopupGroup(undefined)}
          onSave={saveGroup}
          onDelete={popupGroup ? deleteGroup : undefined}
        />
      )}
    </div>
  )
}
